/**
 * The LAB MARKER (wiswiz #171): our own measurement runs are not parents.
 *
 * On 4 and 5 September 2026 about half of Meta's "afrekenen gestart" for the
 * parent campaign were our own Playwright runs on production (7 of 16, then
 * 15 of 25): they fire the pixel with a fixed click id and they fire PostHog,
 * and the ad set optimises on exactly that event, so it was learning from our
 * robots. Every tool of ours now sends this marker (~/bin/wiswiz-lab-context.mjs
 * on the agent server):
 *
 *   cookie  wiswiz_lab=1        and/or        header  X-WisWiz-Lab: 1
 *
 * With the marker the site loads no Google Tag Manager (so no Meta pixel and
 * no TikTok pixel), no Plausible and no PostHog; the backend keeps the flag on
 * the payment row and sends no conversion for it. Without the marker nothing
 * changes. ONE marker, no second path: with it nothing is measured, without
 * it everything is, exactly as before.
 *
 * The browser side can only read the cookie (a request header is invisible
 * to page scripts), so the middleware answers a request that carries the
 * header with the cookie: see `zetLabCookie` in middleware.ts.
 */

export const LAB_COOKIE = "wiswiz_lab";
export const LAB_HEADER = "x-wiswiz-lab";
export const LAB_VALUE = "1";

const LAB_COOKIE_VORM = new RegExp(
  `(?:^|;\\s*)${LAB_COOKIE}=${LAB_VALUE}(?:;|$)`
);

/** Is this page a lab run? Reads document.cookie; unreadable = not a lab. */
export function isLabBrowser(): boolean {
  if (typeof document === "undefined") return false;
  try {
    return LAB_COOKIE_VORM.test(document.cookie);
  } catch {
    /* an iframe without access, storage blocked: then this is a normal visitor */
    return false;
  }
}

/* The exact test the inline preload script in Meetscripts.tsx runs, as one
   string, so both sides agree on the one shape of the marker. */
export const LAB_COOKIE_TEST_JS = LAB_COOKIE_VORM.source;

type LabRequest = {
  headers: { get(name: string): string | null };
  cookies: { get(name: string): { value: string } | undefined };
};

/** Does this request carry the marker, as a header or as a cookie? */
export function isLabRequest(req: LabRequest): boolean {
  return (
    req.headers.get(LAB_HEADER) === LAB_VALUE ||
    req.cookies.get(LAB_COOKIE)?.value === LAB_VALUE
  );
}
